/**
 * A planilha das tentativas de notificação, importada pela supervisão (`OB-08`, ADR-071).
 *
 * O relatório do provedor (e-mail, SMS, WhatsApp, correio) em CSV: primeiro a prévia — o que
 * entra, o que já estava, o que não entra e por quê, e quantas cumprem a etapa —, e só então
 * *Gravar*. É o mesmo desenho da lista de isentos (ADR-065), e o servidor confere cada linha
 * pela regra do domínio, como confere o que vem pela chave do sistema de notificação.
 */
import { useState } from "react";

import { ErroDaApi, api } from "../api/cliente";
import type { PlanilhaDeNotificacoes } from "../api/tipos";
import { Badge, Botao, Painel } from "../componentes/base";
import { Icone } from "../componentes/icones";
import { MODELO_DA_PLANILHA_DE_NOTIFICACOES } from "./protocolo";

function mensagem(falha: unknown, padrao: string): string {
  return falha instanceof ErroDaApi ? falha.message : padrao;
}

function lerTexto(arquivo: File): Promise<string> {
  return new Promise((resolver, recusar) => {
    const leitor = new FileReader();
    leitor.onload = () => resolver(String(leitor.result ?? ""));
    leitor.onerror = () => recusar(leitor.error ?? new Error("leitura"));
    leitor.readAsText(arquivo);
  });
}

function baixarModelo() {
  if (typeof URL.createObjectURL !== "function") return;
  const url = URL.createObjectURL(
    new Blob([`\uFEFF${MODELO_DA_PLANILHA_DE_NOTIFICACOES}\n`], {
      type: "text/csv;charset=utf-8",
    }),
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = "modelo-notificacoes.csv";
  link.click();
  URL.revokeObjectURL(url);
}

export function ImportacaoDeNotificacoes({
  aoGravar,
}: {
  aoGravar: () => void;
}) {
  const [arquivo, definirArquivo] = useState<{
    nome: string;
    conteudo: string;
  } | null>(null);
  const [resultado, definirResultado] = useState<PlanilhaDeNotificacoes | null>(
    null,
  );
  const [recusa, definirRecusa] = useState<string | null>(null);
  const [enviando, definirEnviando] = useState(false);

  async function enviar(conteudo: string, nome: string, aplicar: boolean) {
    definirEnviando(true);
    definirRecusa(null);
    try {
      const r = await api<PlanilhaDeNotificacoes>(
        "/tollvision/notificacoes/planilha",
        { method: "POST", corpo: { conteudo, nome_do_arquivo: nome, aplicar } },
      );
      definirResultado(r);
      if (aplicar) aoGravar();
    } catch (falha) {
      definirResultado(null);
      definirRecusa(mensagem(falha, "Não foi possível ler a planilha."));
    } finally {
      definirEnviando(false);
    }
  }

  async function escolher(lista: FileList | null) {
    const escolhido = lista?.[0];
    if (!escolhido) return;
    const conteudo = await lerTexto(escolhido);
    definirArquivo({ nome: escolhido.name, conteudo });
    await enviar(conteudo, escolhido.name, false);
  }

  return (
    <Painel
      titulo="Importar a planilha do provedor"
      acoes={
        <Botao
          onClick={baixarModelo}
          title="As colunas que a importação lê, com dois exemplos."
        >
          <Icone.Baixar />
          Modelo
        </Botao>
      }
    >
      <p className="text-[11px] text-tinta-3">
        CSV com as colunas <span className="mono">passagem</span> (o id do
        trânsito ou a referência do equipamento),{" "}
        <span className="mono">etapa</span> (8, 15 ou correspondência),{" "}
        <span className="mono">canal</span>, <span className="mono">data</span>{" "}
        (dd/mm/aaaa hh:mm, Brasília) e <span className="mono">recibo</span>; e{" "}
        <span className="mono">resultado</span>, opcional (vazio: enviada).
        Primeiro a prévia; nada é gravado até você confirmar.
      </p>
      <input
        type="file"
        accept=".csv,text/csv"
        aria-label="Planilha de notificações"
        onChange={(e) => void escolher(e.target.files)}
        className="text-[11.5px] text-tinta-2"
      />
      {recusa && (
        <p role="alert" className="text-[11.5px] text-vermelho">
          {recusa}
        </p>
      )}
      {resultado && (
        <section aria-label="Prévia da planilha" className="grid gap-1.5">
          <div className="flex flex-wrap gap-1.5">
            <Badge cor="azul">{`${resultado.linhas} linhas`}</Badge>
            <Badge cor="verde">
              {resultado.aplicada
                ? `${resultado.gravadas} gravadas`
                : `${resultado.validas} entram`}
            </Badge>
            <Badge cor={resultado.cumprem > 0 ? "verde" : "cinza"}>
              {`${resultado.cumprem} cumprem a etapa`}
            </Badge>
            {resultado.repetidas > 0 && (
              <Badge cor="cinza">{`${resultado.repetidas} já registradas`}</Badge>
            )}
            {resultado.problemas.length > 0 && (
              <Badge cor="vermelho">
                {`${resultado.problemas.length} com problema`}
              </Badge>
            )}
          </div>
          {resultado.validas > resultado.cumprem && (
            <p className="text-[11px] text-amarelo">
              {resultado.validas - resultado.cumprem} entram e não cumprem a
              etapa: fora do dia, ou não entregues. Ficam na trilha — a norma
              pede o registro de todas as tentativas.
            </p>
          )}
          {resultado.problemas.length > 0 && (
            <ul className="grid max-h-40 gap-0.5 overflow-auto text-[11px] text-tinta-2">
              {resultado.problemas.slice(0, 50).map((p) => (
                <li key={`${p.linha}-${p.passagem ?? ""}`}>
                  <span className="mono text-tinta-3">linha {p.linha}</span>
                  {p.passagem ? ` · ${p.passagem}` : ""}: {p.motivo}
                </li>
              ))}
            </ul>
          )}
          {!resultado.aplicada && resultado.validas > 0 && arquivo && (
            <Botao
              papel="aprovar"
              disabled={enviando}
              onClick={() => void enviar(arquivo.conteudo, arquivo.nome, true)}
            >
              {enviando
                ? "Gravando…"
                : `Gravar ${resultado.validas} ${resultado.validas === 1 ? "tentativa" : "tentativas"}`}
            </Botao>
          )}
        </section>
      )}
    </Painel>
  );
}
